import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";
import Logo from "@/components/Logo";
import { COMPANY, SERVICE_PAGES, SERVICE_AREAS, AREA_GROUPS } from "@/data";

const cityBySlug = (slug) => SERVICE_AREAS.find((a) => a.slug === slug);

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-navy-dark text-white">
    {/* Big CTA band */}
    <div className="border-b border-white/10">
      <div className="max-w-[1440px] mx-auto px-5 md:px-12 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
        <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl leading-[0.9] tracking-tight">
          Let's build
          <br />
          <span className="text-gold">something great.</span>
        </h2>
        <Link
          to="/contact"
          data-testid="footer-cta-btn"
          className="shrink-0 inline-flex items-center justify-center rounded-full bg-gold hover:bg-gold-hover text-navy-dark font-bold uppercase tracking-wide text-sm px-9 py-4 transition-colors duration-200"
        >
          Get Free Estimate
        </Link>
      </div>
    </div>

    <div className="max-w-[1440px] mx-auto px-5 md:px-12 py-14 grid gap-12 md:grid-cols-5">
      <div className="md:col-span-2">
        <Logo light />
        <p className="mt-5 font-display uppercase tracking-[0.15em] text-gold text-sm">
          {COMPANY.slogan}
        </p>
        <p className="mt-4 font-body text-white/60 max-w-sm">
          Locally owned remodeling & construction serving {COMPANY.serviceArea}.
        </p>
        <div className="mt-6 flex gap-3">
          <a href={COMPANY.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram"
            className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy-dark transition-colors duration-200">
            <Instagram size={20} />
          </a>
          <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook"
            className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy-dark transition-colors duration-200">
            <Facebook size={20} />
          </a>
        </div>
      </div>

      <div>
        <h3 className="font-display uppercase tracking-wide text-white/50 text-sm mb-5">Services</h3>
        <ul className="space-y-3">
          {SERVICE_PAGES.map((s) => (
            <li key={s.slug}>
              <Link to={`/services/${s.slug}`} className="font-body text-white/75 hover:text-gold transition-colors duration-200 text-[15px]">
                {s.nav}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="font-display uppercase tracking-wide text-white/50 text-sm mb-5">
          <Link to="/service-areas" className="hover:text-gold transition-colors">Service Areas</Link>
        </h3>
        {AREA_GROUPS.map((g) => (
          <div key={g.state} className="mb-4">
            <div className="font-body text-white/40 text-xs uppercase tracking-wide mb-2">{g.state}</div>
            <ul className="space-y-2">
              {g.cities.map((slug) => (
                <li key={slug}>
                  <Link to={`/service-areas/${slug}`} className="font-body text-white/75 hover:text-gold transition-colors duration-200 text-[15px]">
                    {cityBySlug(slug)?.city}, {cityBySlug(slug)?.state}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div>
        <h3 className="font-display uppercase tracking-wide text-white/50 text-sm mb-5">Get in Touch</h3>
        <ul className="space-y-4 font-body text-white/75 text-sm">
          <li>
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-3 hover:text-gold transition-colors duration-200">
              <Phone size={17} className="text-gold" /> {COMPANY.phone}
            </a>
          </li>
          <li>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 hover:text-gold transition-colors duration-200">
              <Mail size={17} className="text-gold" /> {COMPANY.email}
            </a>
          </li>
          <li className="flex items-start gap-3">
            <MapPin size={17} className="text-gold shrink-0 mt-0.5" /> Serving {COMPANY.serviceArea}
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="max-w-[1440px] mx-auto px-5 md:px-12 py-6 flex flex-col sm:flex-row justify-between gap-2 text-white/45 text-sm font-body">
        <span>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</span>
        <span>{COMPANY.website}</span>
      </div>
    </div>
  </footer>
);

export default Footer;
